import { useEffect, useRef, useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import {
  useMutateSuite,
  useSuiteMemberCandidates,
} from '@/hooks/api/automationSuites/useAutomationSuites.ts';
import type { SuiteDetail, SuiteMember } from '@/types/automationSuites.ts';
import type { AutomationDocumentationSummary } from '@/types/automations.ts';
import {
  ExactVersion,
  SuiteError,
  SuitePagination,
} from '@/pages/AutomationSuites/SuiteShared.tsx';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { assertNewSuiteMembership } from '@/utils/suiteMembership.ts';

type Form =
  { kind: 'add' } | { kind: 'edit'; member: SuiteMember } | { kind: 'remove'; member: SuiteMember };

export default function SuiteMembers({
  suite,
  page,
  onPage,
  canEdit,
  refresh,
}: {
  suite: SuiteDetail;
  page: number;
  onPage: (page: number) => void;
  canEdit: boolean;
  refresh: () => void;
}) {
  const [form, setForm] = useState<Form | null>(null);
  return (
    <Stack>
      <Group justify="space-between">
        <Text>Explicit members · multiple entrypoints are allowed.</Text>
        {canEdit && <Button onClick={() => setForm({ kind: 'add' })}>Add member</Button>}
      </Group>
      {!suite.members.results.length ? (
        <Text>This page has no members. Empty Suites are valid.</Text>
      ) : (
        <Table.ScrollContainer minWidth={860}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Automation / purpose</Table.Th>
                <Table.Th>Entrypoint</Table.Th>
                <Table.Th>Versions</Table.Th>
                <Table.Th>
                  Blueprint health
                  <Text size="xs" fw="normal" c="dimmed">
                    Advisory; Release documents are reviewed separately.
                  </Text>
                </Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {suite.members.results.map((member) => (
                <Table.Tr key={member.automation_id}>
                  <Table.Td>
                    {member.availability === 'deleted' ? (
                      member.name
                    ) : (
                      <Anchor component={Link} to={`/automations/${member.automation_id}`}>
                        {member.name}
                      </Anchor>
                    )}
                    <Text size="sm">{member.purpose ?? 'Purpose unavailable in this scope'}</Text>
                    <Badge color={member.availability === 'active' ? 'blue' : 'gray'}>
                      {member.availability}
                    </Badge>
                    {member.display_order !== null && (
                      <Text size="xs">Order: {member.display_order}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>{member.is_entrypoint ? 'Yes' : 'No'}</Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      Latest:{' '}
                      <ExactVersion
                        id={member.latest_version_id}
                        number={member.latest_version_number}
                      />
                    </Text>
                    <Text size="sm">
                      Published:{' '}
                      <ExactVersion
                        id={member.published_version_id}
                        number={member.published_version_number}
                      />
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <MemberBlueprintHealth
                      member={member}
                      basis="latest"
                      summary={member.blueprint_health.latest_head}
                    />
                    <MemberBlueprintHealth
                      member={member}
                      basis="published"
                      summary={member.blueprint_health.published_head}
                    />
                  </Table.Td>
                  <Table.Td>
                    {canEdit && (
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="default"
                          aria-label={`Edit ${member.name}`}
                          onClick={() => setForm({ kind: 'edit', member })}
                        >
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="default"
                          aria-label={`Remove ${member.name}`}
                          onClick={() => setForm({ kind: 'remove', member })}
                        >
                          Remove
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <SuitePagination data={suite.members} onPage={onPage} />
      {form && (
        <MemberForm
          key={form.kind === 'add' ? 'add' : `${form.kind}:${form.member.automation_id}`}
          suite={suite}
          form={form}
          canEdit={canEdit}
          close={() => setForm(null)}
          refresh={refresh}
          page={page}
          onPage={onPage}
        />
      )}
    </Stack>
  );
}

const blueprintStatus = {
  missing: { label: 'No saved Blueprint', color: 'gray' },
  review_needed: { label: 'Blueprint needs review', color: 'yellow' },
  reviewed_aligned: { label: 'Blueprint reviewed', color: 'green' },
} as const;

function MemberBlueprintHealth({
  member,
  basis,
  summary,
}: {
  member: SuiteMember;
  basis: 'latest' | 'published';
  summary: AutomationDocumentationSummary | null;
}) {
  const versionId = basis === 'latest' ? member.latest_version_id : member.published_version_id;
  const versionNumber =
    basis === 'latest' ? member.latest_version_number : member.published_version_number;
  const label = basis === 'latest' ? 'Latest head' : 'Published head';
  if (!versionId) {
    return (
      <Text size="sm" c="dimmed">
        {label}: No {basis} head
      </Text>
    );
  }
  const health = summary ?? {
    automation_version_id: versionId,
    event_id: null,
    revision_id: null,
    alignment_status: 'missing' as const,
    reason_codes: [],
  };
  const status = blueprintStatus[health.alignment_status];
  const inspect =
    member.availability === 'deleted' ? null : (
      <Anchor
        component={Link}
        to={`/automations/${member.automation_id}/editor?blueprintVersion=${encodeURIComponent(versionId)}`}
        size="xs"
      >
        Inspect Blueprint
      </Anchor>
    );
  return (
    <Stack gap={2} mb="xs">
      <Text size="sm">
        {label}: v{versionNumber ?? '?'}
      </Text>
      <Group gap="xs">
        <Badge color={status.color} size="sm">
          {status.label}
        </Badge>
        {inspect}
      </Group>
      {member.availability === 'deleted' && (
        <Text size="xs" c="dimmed">
          Inspection is unavailable because this automation was deleted.
        </Text>
      )}
    </Stack>
  );
}

function MemberForm({
  suite,
  form,
  canEdit,
  close,
  refresh,
  page,
  onPage,
}: {
  suite: SuiteDetail;
  form: Form;
  canEdit: boolean;
  close: () => void;
  refresh: () => void;
  page: number;
  onPage: (page: number) => void;
}) {
  const { orgId } = useOrgProject();
  const api = useApiClient();
  const [fence] = useState(suite.head_revision);
  const member = form.kind === 'add' ? null : form.member;
  const [automationId, setAutomationId] = useState<string | null>(member?.automation_id ?? null);
  const [entrypoint, setEntrypoint] = useState(member?.is_entrypoint ?? false);
  const [purpose, setPurpose] = useState(member?.purpose ?? '');
  const [order, setOrder] = useState<number | string>(member?.display_order ?? '');
  const [candidatePage, setCandidatePage] = useState(1);
  const candidates = useSuiteMemberCandidates(suite.project_id, candidatePage, form.kind === 'add');
  const mutation = useMutateSuite();
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<unknown>(null);
  const mounted = useRef(true);
  const discovery = useRef<AbortController | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      discovery.current?.abort();
    };
  }, []);
  const stale = suite.head_revision !== fence;
  const authority = useRef({ canEdit, head: suite.head_revision });
  authority.current = { canEdit, head: suite.head_revision };
  const submit = async () => {
    if (!automationId || stale || !canEdit || checking || mutation.isPending) return;
    try {
      if (form.kind === 'add') {
        setChecking(true);
        setCheckError(null);
        const controller = new AbortController();
        discovery.current = controller;
        await assertNewSuiteMembership(api, automationId, suite.id, controller.signal);
        if (!mounted.current) return;
        if (!authority.current.canEdit || authority.current.head !== fence) {
          throw new Error(
            'Suite changed during membership discovery. Review its latest state before adding a member.',
          );
        }
      }
      await mutation.mutateAsync({
        orgId,
        projectId: suite.project_id,
        suiteId: suite.id,
        change:
          form.kind === 'remove'
            ? {
                kind: 'remove',
                automationId,
                body: { expected_head_revision: fence },
              }
            : {
                kind: 'member',
                automationId,
                body: {
                  expected_head_revision: fence,
                  is_entrypoint: entrypoint,
                  purpose,
                  display_order: typeof order === 'number' ? order : null,
                },
              },
      });
      if (mounted.current) {
        close();
        if (form.kind === 'remove' && suite.members.results.length === 1 && page > 1)
          onPage(page - 1);
      }
    } catch (failure) {
      if (mounted.current) setCheckError(failure);
    } finally {
      discovery.current = null;
      if (mounted.current) setChecking(false);
    }
  };
  const options =
    candidates.data?.results
      .filter(
        (automation) =>
          !automation.retired_at &&
          !suite.members.results.some((row) => row.automation_id === automation.id),
      )
      .map((automation) => ({ value: automation.id, label: automation.name })) ?? [];
  const dismiss = () => {
    if (mutation.isPending) return;
    discovery.current?.abort();
    close();
  };
  return (
    <StyledModal
      opened
      onClose={dismiss}
      title={
        form.kind === 'remove'
          ? 'Remove Suite member'
          : form.kind === 'add'
            ? 'Add Suite member'
            : 'Edit Suite member'
      }
    >
      {stale && (
        <Text c="orange">
          Suite changed while this form was open. Close and reopen to review the latest state.
        </Text>
      )}
      {!canEdit && <Text c="orange">This Suite is no longer editable.</Text>}
      {form.kind === 'add' && (
        <>
          <Text size="sm">
            Search within this page of active project automations. Use the page controls for more
            candidates.
          </Text>
          {candidates.isError && (
            <SuiteError error={candidates.error} retry={() => void candidates.refetch()} />
          )}
          <Select
            searchable
            label="Automation"
            data={options}
            value={automationId}
            onChange={setAutomationId}
            disabled={candidates.isPending || mutation.isPending || checking}
          />
          {candidates.data && (
            <SuitePagination
              data={candidates.data}
              disabled={mutation.isPending || checking}
              onPage={(next) => {
                setCandidatePage(next);
                setAutomationId(null);
              }}
            />
          )}
        </>
      )}
      {form.kind === 'remove' ? (
        <Text>
          Remove {member?.name} from this Suite? The automation and memberships in other Suites are
          retained.
        </Text>
      ) : (
        <>
          <Checkbox
            label="Entrypoint"
            checked={entrypoint}
            onChange={(event) => setEntrypoint(event.currentTarget.checked)}
            disabled={mutation.isPending || checking}
          />
          <Textarea
            label="Member purpose"
            value={purpose}
            maxLength={1024}
            onChange={(event) => setPurpose(event.currentTarget.value)}
            disabled={mutation.isPending || checking}
          />
          <NumberInput
            label="Display order (optional)"
            min={0}
            max={1_000_000}
            allowDecimal={false}
            value={order}
            onChange={setOrder}
            disabled={mutation.isPending || checking}
          />
        </>
      )}
      {checkError !== null && (
        <>
          <SuiteError error={checkError} />
          <Button variant="default" onClick={refresh}>
            Refresh Suite
          </Button>
        </>
      )}
      <Group>
        <Button
          disabled={!automationId || stale || !canEdit}
          loading={mutation.isPending || checking}
          onClick={() => void submit()}
        >
          {form.kind === 'remove' ? 'Confirm removal' : 'Save member'}
        </Button>
        <Button variant="default" disabled={mutation.isPending} onClick={dismiss}>
          Cancel
        </Button>
      </Group>
    </StyledModal>
  );
}
